/**
 * Hugin's advice in Vaktplan (issue #131, owner 2026-10-13: «this is a super important feature to
 * show off in demo»). Hugin advises on every change without being asked: who fits an open shift
 * best and why, what each Varsler entry's fix costs, what a move, a reassignment, a deletion or a
 * length change did, and a few things worth a look.
 *
 * Deterministic, and no model call (decided 2026-10-14 overnight, recommended option; owner may
 * reverse), as the phone's Kontroller (#134) is. Every rule here is one the app already checks:
 * cover.ts candidates() for absence, availability, an overlapping shift and the 11 h rest; core's
 * checkWeek and checkShift (web/src/rules) for the week ceiling, the 35 h rest, every other Sunday
 * and the day's length; the agreed week from the person's membership; the server's own rule
 * entries for what a saved change did. The words are fixed sentences; the ranking is an order over
 * those facts, never a score a model made. Tested in advice.test.ts.
 */
import type { ApiAvailability } from '@/api/availability';
import type { ApiAbsence, ApiOffer, ApiShift, ApiSwap } from '@/api/data';
import { T } from '@/i18n';
import { type Entry, apiEntries, oslo, ruleText, tenantRules, toShiftLike } from '@/rules';
import { DN, fmtDs, fullName, num } from '@/ui/format';
import { type RuleEntry, addDays, checkShift, checkWeek, dow, isoWeek, mondayOf } from '@timefly/core';
import { type Candidate, candidates } from './cover';
import { availabilityFor } from './coverFlow';
import { type Row, dateOf, timeText, typeLabel } from './model';

type Tr = (k: string, v?: Record<string, string | number>) => string;

/** What one choice does when pressed: a route the page already calls. */
export type Apply =
  | { kind: 'give'; shiftId: number; userId: number }
  | { kind: 'decide'; what: 'offer' | 'swap'; id: number; ok: boolean }
  /** a screen to open, for the suggestions outside Vaktplan (the project budgets, #132) */
  | { kind: 'open'; to: string };
/** One option Hugin offers: its button, the cost in one or two lines, and whether it is safe. */
export interface Choice {
  key: string;
  label: string;
  lines: string[];
  safe: boolean;
  apply: Apply;
  /** the person a give names, for the verdict */
  who?: string;
  /** a give: the person's availability for the shift is on record (review R4) */
  known?: boolean;
}
/** Hugin's word on one Varsler entry: the verdict, the cost to the people in it, and the options. */
export interface Advice {
  tone: 'ok' | 'warn';
  verdict: string;
  lines: string[];
  choices: Choice[];
}

/** One person as a taker of a shift: candidates()'s verdict plus the week and the rules taking it would add. */
export interface Option extends Candidate {
  /** their planned hours in the shift's ISO week, before and after taking it */
  before: number;
  after: number;
  /** core rule entries (week ceiling, 35 h rest, Sundays, day length) taking it would add */
  adds: Entry[];
  /** shifts of this shift's type they hold in the plan the page has loaded */
  seen: number;
  /** clear: nothing in the way, nothing added, not over the agreed week */
  safe: boolean;
  /** the one-line reason, fixed words; set by ranked() */
  reason: string;
}

// codes with a sentence in the catalogue; the rest of core's entries are the grid's own. Not core's
// 35 h rest: it reads only the gaps between shifts, so a week whose plan ends on a Thursday has
// "no rest" (next week empty, as a demo's is). The server judges that one on save, and the
// evaluation after the save says what it found.
const WEEK_CODES = new Set(['week_over_max', 'sundays']);
const DAY_CODES = new Set(['day_over_13', 'day_over_max']);
const round1 = (n: number) => Math.round(n * 10) / 10;
const when = (s: Pick<ApiShift, 'start_time' | 'end_time'>) => {
  const d = dateOf(s as ApiShift);
  return `${DN[dow(d)]} ${fmtDs(d)} ${timeText(s as ApiShift)}`;
};
const started = (s: ApiShift, now: number) => !!s.punch_in_time || Date.parse(s.start_time) <= now;

/** Core's entry in the catalogue's words: the day's length codes read the server's own lines. */
const toEntry = (e: RuleEntry): Entry => ({
  code: e.code,
  sev: e.sev,
  args: e.args,
  source: DAY_CODES.has(e.code) ? 'api' : 'core',
});

/** A person's week, as core counts it: planned span per shift, the agreed week, the week rules. */
function week(row: Row, monday: string, list: ApiShift[]) {
  return checkWeek(personOf(row), monday, list.map(toShiftLike), tenantRules());
}
// a row always has its person from buildGrid; one made by hand may carry the week alone
const personOf = (row: Row) => row.person ?? { id: String(row.user.id), norm: row.week?.norm ?? null };

export interface Balance {
  userId: number;
  name: string;
  before: number;
  after: number;
  norm: number | null;
  /** week rules the change adds, and those it removes */
  adds: Entry[];
  gone: Entry[];
}
/**
 * One person's ISO week before and after a change: `drop` leaves their plan, `add` joins it with
 * them on it. Read from the shifts the page holds; nothing is written.
 */
export function balance(
  row: Row,
  shifts: ApiShift[],
  monday: string,
  drop: number | null,
  add: ApiShift | null,
): Balance {
  const mine = shifts.filter((x) => x.user_id === row.user.id);
  const after = [
    ...mine.filter((x) => x.id !== drop && x.id !== add?.id),
    ...(add ? [{ ...add, user_id: row.user.id }] : []),
  ];
  const b = week(row, monday, mine);
  const a = week(row, monday, after);
  const pick = (es: RuleEntry[]) => es.filter((e) => WEEK_CODES.has(e.code));
  const codes = (es: RuleEntry[]) => new Set(pick(es).map((e) => e.code));
  const day = add
    ? checkShift(
        { ...toShiftLike({ ...add, user_id: row.user.id } as never), punchIn: null, punchOut: null },
        personOf(row),
        null,
        tenantRules(),
      )
        .filter((e) => DAY_CODES.has(e.code))
        .map(toEntry)
    : [];
  return {
    userId: row.user.id,
    name: fullName(row.user),
    before: b.hours,
    after: a.hours,
    norm: personOf(row).norm,
    adds: [
      ...pick(a.entries)
        .filter((e) => !codes(b.entries).has(e.code))
        .map(toEntry),
      ...day,
    ],
    gone: pick(b.entries)
      .filter((e) => !codes(a.entries).has(e.code))
      .map(toEntry),
  };
}

/** "Kari Hansen: 32 → 40 t, avtalt 40" and how far from it, or the hours alone without an agreed week. */
export function balanceLine(b: Pick<Balance, 'name' | 'before' | 'after' | 'norm'>): string {
  const base =
    b.norm == null
      ? T('{name}: {from} → {to} t denne uka', {
          name: b.name,
          from: num(round1(b.before)),
          to: num(round1(b.after)),
        })
      : T('{name}: {from} → {to} t av avtalt {norm}', {
          name: b.name,
          from: num(round1(b.before)),
          to: num(round1(b.after)),
          norm: num(b.norm),
        });
  if (b.norm == null) return base;
  const d = round1(b.after - b.norm);
  return d > 0
    ? `${base} (${T('{h} t over', { h: num(d) })})`
    : d < 0
      ? `${base} (${T('{h} t under', { h: num(-d) })})`
      : base;
}

const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

/** The one-line reason for a taker, from the facts only: room in the week, the type, rest, what is in the way. */
export function reasonOf(
  o: Omit<Option, 'reason'>,
  t: Tr,
  typeName: string | null,
  showHours: boolean,
): string {
  // blocked reads as the list always read, «fravær, har vakt da»
  if (o.kind === 'blocked') return o.why.map((w) => T(w)).join(', ');
  const parts: string[] = [];
  if (showHours && o.norm != null) {
    const room = round1(o.norm - o.after);
    parts.push(
      room > 0
        ? T('{h} t under avtalt uke etter vakten', { h: num(room) })
        : room === 0
          ? T('når akkurat avtalt uke')
          : T('{h} t over avtalt uke etter vakten', { h: num(-room) }),
    );
  }
  if (o.seen > 0 && typeName) parts.push(T('har hatt {type} før', { type: typeName }));
  const rest = o.why.find((w) => w.endsWith('t hvile'));
  parts.push(rest ? T('bare {rest}', { rest }) : T('ingen hvilekonflikt'));
  for (const w of o.why) if (w !== rest && w !== 'over avtalt uke') parts.push(T(w));
  for (const e of o.adds) parts.push(ruleText(t, e));
  return cap(parts.join(', '));
}

/**
 * Who could take shift `s`, best first, each with the one-line reason. The order: nothing in the
 * way, then fewest rules touched, then the most room left under the agreed week, then who has had
 * this type before, then the name. Blocked people come last and keep no button. The reader is left
 * out (`me`): the manager planning is not Hugin's suggestion for their own plan, and in a demo the
 * visitor, with an empty week, would top every list (decided 2026-10-14 overnight, recommended
 * option; owner may reverse). The person picker still offers them.
 */
export function ranked(
  s: ApiShift,
  rows: Row[],
  shifts: ApiShift[],
  t: Tr,
  showHours = true,
  me: number | null = null,
): Option[] {
  const monday = mondayOf(dateOf(s));
  const typeName = typeLabel(s.shiftType) ?? null;
  const out = candidates(s, rows, shifts).flatMap((c) => {
    const row = rows.find((r) => r.user.id === c.userId);
    if (!row || c.userId === me) return [];
    const b = balance(row, shifts, monday, s.id, s);
    // the agreed week is judged here on the shift's own ISO week; cover.ts reads the window on screen,
    // which in To uker and Måned is a pro-rata figure, so its «over avtalt uke» gives way to this one
    const over = b.norm != null && b.after > b.norm + 0.05;
    const why = [...c.why.filter((w) => w !== 'over avtalt uke'), ...(over ? ['over avtalt uke'] : [])];
    const kind: Candidate['kind'] =
      c.kind === 'blocked' ? 'blocked' : why.length || b.adds.length ? 'review' : 'clear';
    const seen = shifts.filter(
      (x) => x.user_id === c.userId && s.shift_type_id != null && x.shift_type_id === s.shift_type_id,
    ).length;
    const o = {
      ...c,
      why,
      kind,
      hours: b.before,
      norm: b.norm,
      before: b.before,
      after: b.after,
      adds: b.adds,
      seen,
      safe: kind === 'clear',
    };
    return [{ ...o, reason: reasonOf(o, t, typeName, showHours) }];
  });
  const rank = { clear: 0, review: 1, blocked: 2 };
  const room = (o: Option) => (o.norm == null ? Number.NEGATIVE_INFINITY : o.norm - o.after);
  return out.sort(
    (a, b) =>
      rank[a.kind] - rank[b.kind] ||
      a.adds.filter((e) => e.sev === 'd').length - b.adds.filter((e) => e.sev === 'd').length ||
      a.why.length + a.adds.length - (b.why.length + b.adds.length) ||
      (showHours ? room(b) - room(a) : 0) ||
      b.seen - a.seen ||
      a.name.localeCompare(b.name, 'nb'),
  );
}

export interface Ctx {
  rows: Row[];
  shifts: ApiShift[];
  offers?: ApiOffer[];
  swaps?: ApiSwap[];
  t: Tr;
  /** timesheet.view (timeliste.se): hours may be said */
  showHours: boolean;
  /** vaktplan.endre in planning: a choice may be offered */
  canGive: boolean;
  now: number;
  /** the reader, left out of the takers */
  me?: number | null | undefined;
  /** the availability rows, so «trygt» is said only where availability is on record (review R4) */
  avail?: ApiAvailability[] | undefined;
}

/**
 * Who may be offered as a one-press suggestion: not blocked, and not short of the 11 h rest. A rest
 * breach is a choice the manager makes in Finn erstatter with the conflict in front of them, never
 * a quick button (review of #147, F1). Pressing one opens the preview; nothing is written from here.
 */
const quick = (o: Option) =>
  o.kind !== 'blocked' && !o.why.some((w) => w.endsWith('t hvile')) && !o.adds.some((e) => e.sev === 'd');

/** A give-away for a shift: the best takers that are not blocked, two or three, and the holder's week after. */
function giveAway(s: ApiShift, ctx: Ctx, n = 3): { lines: string[]; choices: Choice[] } {
  const holder = ctx.rows.find((r) => r.user.id === s.user_id);
  const lines =
    holder && ctx.showHours
      ? [balanceLine(balance(holder, ctx.shifts, mondayOf(dateOf(s)), s.id, null))]
      : [];
  const choices = ranked(s, ctx.rows, ctx.shifts, ctx.t, ctx.showHours, ctx.me ?? null)
    .filter(quick)
    .slice(0, n)
    .map((o) => ({
      key: `give-${s.id}-${o.userId}`,
      label: T('Se endring: {name}', { name: o.name }),
      lines: [o.reason],
      safe: o.safe,
      apply: { kind: 'give' as const, shiftId: s.id, userId: o.userId },
      who: o.name,
      known: availabilityFor(ctx.avail ?? [], o.userId, s).kind === 'reported',
    }));
  return { lines, choices };
}

// «trygt» only when nothing in the plan is in the way and availability is on record; the same
// evidence Finn erstatter sorts by (review R4)
const verdictOf = (choices: Choice[]): Pick<Advice, 'tone' | 'verdict'> =>
  choices[0]?.safe && choices[0].known
    ? {
        tone: 'ok',
        verdict: T('Trygt å gi bort: {name} kan ta vakten uten nye varsler.', {
          name: choices[0].who ?? '',
        }),
      }
    : choices[0]?.safe
      ? {
          tone: 'warn',
          verdict: T('{name} har ingen kjente konflikter i planen. Tilgjengelighet er ikke oppgitt.', {
            name: choices[0].who ?? '',
          }),
        }
      : choices.length
        ? { tone: 'warn', verdict: T('Alle som kan ta vakten får et varsel. Se hva hvert valg koster.') }
        : {
            tone: 'warn',
            verdict: T('Ingen kan ta vakten uten fravær, sperret tid eller en annen vakt i veien.'),
          };

/** The shift codes the person, not the shift, is the problem for: another person fixes them. */
const PERSON_CODES = new Set(['rest', 'youth_rest', 'youth_night', 'youth_evening']);
/** The week codes moving one of the week's shifts can fix. */
const WEEK_FIX = new Set([
  'weekly_over',
  'weekly_over_cap',
  'weekly_over_max',
  'week_over_avg',
  'weekly_over_48',
  'week_over_max',
  'youth_week_over',
  'weekrest',
  'sundays',
]);
/** The day's length: the fix is a shorter shift, not another person. */
const LENGTH_CODES = new Set(['day_over', 'day_over_max', 'day_over_13', 'youth_day_over']);

/**
 * Moves that ease one person's week: each of their shifts that week not yet started, given to its
 * best taker, safe ones first, then the most room the taker keeps. Two or three.
 */
export function weekMoves(userId: number, monday: string, code: string | null, ctx: Ctx, n = 3): Choice[] {
  const sunday = addDays(monday, 6);
  const mine = ctx.shifts.filter((x) => {
    const d = dateOf(x);
    return x.user_id === userId && d >= monday && d <= sunday && !started(x, ctx.now);
  });
  const list = code === 'sundays' ? mine.filter((x) => dateOf(x) === sunday) : mine;
  const holder = ctx.rows.find((r) => r.user.id === userId);
  const pairs = list.flatMap((s) => {
    const best = ranked(s, ctx.rows, ctx.shifts, ctx.t, ctx.showHours, ctx.me ?? null).find(quick);
    if (!best) return [];
    const mineAfter =
      holder && ctx.showHours ? balanceLine(balance(holder, ctx.shifts, monday, s.id, null)) : null;
    return [
      {
        best,
        choice: {
          key: `move-${s.id}-${best.userId}`,
          label: T('Se endring: {when} til {name}', { when: when(s), name: best.name }),
          lines: [best.reason, ...(mineAfter ? [mineAfter] : [])],
          safe: best.safe,
          apply: { kind: 'give' as const, shiftId: s.id, userId: best.userId },
        } satisfies Choice,
      },
    ];
  });
  const room = (o: Option) => (o.norm == null ? 0 : o.norm - o.after);
  return pairs
    .sort((a, b) => Number(b.best.safe) - Number(a.best.safe) || room(b.best) - room(a.best))
    .slice(0, n)
    .map((p) => p.choice);
}

/** What Hugin says about one Varsler entry, or null where a person is not the fix (a punch, a lunch). */
export function adviceFor(
  item: {
    group: string;
    shiftId?: number | undefined;
    person?: number | undefined;
    rule?: Entry | undefined;
    code?: string | undefined;
    day?: string | undefined;
    request?: { what: 'offer' | 'swap'; id: number } | undefined;
  },
  ctx: Ctx,
): Advice | null {
  const s = item.shiftId != null ? ctx.shifts.find((x) => x.id === item.shiftId) : undefined;
  // a request: what approving it does to both weeks; the button is the Varsler page's own route
  if (item.request) {
    const lines: string[] = [];
    let warn = false;
    const add = (userId: number | null | undefined, drop: ApiShift | null, take: ApiShift | null) => {
      const row = ctx.rows.find((r) => r.user.id === userId);
      const ref = take ?? drop;
      if (!row || !ref) return;
      const b = balance(row, ctx.shifts, mondayOf(dateOf(ref)), drop?.id ?? null, take);
      // a rule the change adds is said to everyone; the hours only to who may see them
      if ((b.norm != null && b.after > b.norm + 0.05 && b.after > b.before) || b.adds.length) warn = true;
      if (ctx.showHours) lines.push(balanceLine(b));
      lines.push(...b.adds.map((e) => `${b.name}: ${ruleText(ctx.t, e)}`));
    };
    if (item.request.what === 'offer') {
      const o = ctx.offers?.find((x) => x.id === item.request?.id);
      const sh = o?.shift ?? ctx.shifts.find((x) => x.id === o?.shift_id) ?? null;
      if (!o || !sh || !o.accepted_by) return null;
      add(o.offered_by, sh, null);
      add(o.accepted_by, null, sh);
    } else {
      const w = ctx.swaps?.find((x) => x.id === item.request?.id);
      if (!w?.shiftOne || !w.shiftTwo) return null;
      add(w.shiftOne.user_id, w.shiftOne, w.shiftTwo);
      add(w.shiftTwo.user_id, w.shiftTwo, w.shiftOne);
    }
    return {
      tone: warn ? 'warn' : 'ok',
      verdict: warn
        ? T('Godkjent går noen over avtalt uke eller får et varsel.')
        : T('Godkjent holder begge innenfor det som er avtalt.'),
      lines,
      choices: ctx.canGive
        ? [
            {
              key: `ok-${item.request.what}-${item.request.id}`,
              label: T('Godkjenn'),
              lines: [],
              safe: !warn,
              apply: { kind: 'decide', what: item.request.what, id: item.request.id, ok: true },
            },
          ]
        : [],
    };
  }
  // a week rule: move one of that week's shifts to someone with room
  if (item.person != null && item.rule && WEEK_FIX.has(item.rule.code) && item.rule.week) {
    // the verdict is the same for who may only read; the buttons are for who may plan
    const moves = weekMoves(item.person, item.rule.week, item.rule.code, ctx);
    return {
      tone: moves[0]?.safe ? 'ok' : 'warn',
      verdict: moves[0]?.safe
        ? T('Kan løses: gi bort én vakt den uka. Valget under gir ingen nye varsler.')
        : moves.length
          ? T('Hver vakt du gir bort koster noe for den som tar den. Velg det minste.')
          : T('Ingen av vaktene den uka kan gis bort: de er i gang, eller ingen kan ta dem.'),
      lines: [],
      choices: ctx.canGive ? moves : [],
    };
  }
  // a day under the need: the open shifts that day, each to its best taker
  if (item.group === 'staff' && item.day) {
    const open = ctx.shifts.filter(
      (x) => x.user_id == null && dateOf(x) === item.day && !started(x, ctx.now),
    );
    const choices = open.flatMap((x) => giveAway(x, ctx, 1).choices);
    return choices.length ? { ...verdictOf(choices), lines: [], choices: ctx.canGive ? choices : [] } : null;
  }
  if (!s || s.user_id == null || started(s, ctx.now)) return null;
  if (item.group === 'rules' && item.code && LENGTH_CODES.has(item.code))
    return {
      tone: 'warn',
      verdict: T('Gjelder lengden på vakten, ikke personen. Kort den ned under Endre.'),
      lines: [],
      choices: [],
    };
  const personal =
    item.group === 'absence' ||
    item.group === 'avail' ||
    (item.group === 'rules' && item.code != null && PERSON_CODES.has(item.code));
  if (!personal) return null;
  const g = giveAway(s, ctx);
  return { ...verdictOf(g.choices), lines: g.lines, choices: ctx.canGive ? g.choices : [] };
}

export interface Evaluation {
  id: number;
  tone: 'ok' | 'warn';
  title: string;
  lines: string[];
  choices: Choice[];
  /** the bar's first line, the result (planning-hugin delivery «ac-strip», owner 2026-10-18) */
  headline: string;
  /** the bar's second line: what was done, the hours and the new warnings, in one line */
  detail: string;
  /** the same facts, for «Se detaljer»: each person's week, the new warnings with their severity, the solved */
  balances: string[];
  added: { text: string; sev: 'w' | 'd' }[];
  gone: string[];
}
const same = (a: ApiShift, b: ApiShift) =>
  a.user_id === b.user_id && a.start_time === b.start_time && a.end_time === b.end_time;

/**
 * What a saved change did (issue #131 item 3): `before` is the shift as it was read before the
 * write, `after` the same shift in the plan now, or null once it is deleted. Null while the plan
 * still shows the old shift (the read after the write has not come back). The rule lines are the
 * server's own entries on the shift, before and after; the hours are each person's ISO week.
 */
export function evaluate(before: ApiShift, after: ApiShift | null, ctx: Ctx): Evaluation | null {
  if (after && same(before, after)) return null;
  const lines: string[] = [];
  const people = [...new Set([before.user_id, after?.user_id].filter((x): x is number => x != null))];
  const names: string[] = [];
  const short: string[] = [];
  for (const uid of people) {
    const row = ctx.rows.find((r) => r.user.id === uid);
    if (!row || !ctx.showHours) continue;
    const ref = after?.user_id === uid ? after : before;
    const monday = mondayOf(dateOf(ref));
    // the plan now holds `after`; the week before the change had `before` in its place
    const now = ctx.shifts.filter((x) => x.user_id === uid);
    const then = [...now.filter((x) => x.id !== before.id), ...(before.user_id === uid ? [before] : [])];
    const line = balanceLine({
      name: fullName(row.user),
      before: week(row, monday, then).hours,
      after: week(row, monday, now).hours,
      norm: personOf(row).norm,
    });
    lines.push(line);
    names.push(fullName(row.user));
    short.push(line);
  }
  const was = apiEntries(before);
  const is = after ? apiEntries(after) : [];
  const added = is.filter((e) => !was.some((x) => x.code === e.code));
  const gone = after ? was.filter((e) => !is.some((x) => x.code === e.code)) : [];
  for (const e of added) lines.push(T('Nytt varsel: {text}', { text: ruleText(ctx.t, e) }));
  for (const e of gone) lines.push(T('Løst: {text}', { text: ruleText(ctx.t, e) }));
  if (after && !added.length && !gone.length) lines.push(T('Ingen nye regelvarsler.'));
  const choices =
    ctx.canGive && after?.user_id != null && added.length && !started(after, ctx.now)
      ? ranked(after, ctx.rows, ctx.shifts, ctx.t, ctx.showHours, ctx.me ?? null)
          .filter((o) => o.safe)
          .slice(0, 2)
          .map((o) => ({
            key: `instead-${after.id}-${o.userId}`,
            label: T('Se endring: heller {name}', { name: o.name }),
            lines: [o.reason],
            safe: true,
            apply: { kind: 'give' as const, shiftId: after.id, userId: o.userId },
          }))
      : [];
  const title = !after
    ? T('Hugin om slettingen')
    : before.user_id !== after.user_id
      ? T('Hugin om byttet')
      : Date.parse(after.end_time) - Date.parse(after.start_time) !==
          Date.parse(before.end_time) - Date.parse(before.start_time)
        ? T('Hugin om den nye lengden')
        : T('Hugin om flyttingen');
  // the bar's two lines: the result first, then the change, the weeks and the warnings in one line
  const who =
    names.length > 0
      ? names.join(` ${T('og')} `)
      : (() => {
          const u = (after ?? before).user;
          return u ? fullName(u) : '';
        })();
  const forWho = (text: string) => (who ? `${text} ${T('for {who}', { who })}` : text);
  const headline = added.length
    ? forWho(
        added.length === 1 ? T('Hugin: 1 nytt varsel') : T('Hugin: {n} nye varsler', { n: added.length }),
      )
    : gone.length
      ? forWho(
          gone.length === 1 ? T('Hugin: 1 varsel løst') : T('Hugin: {n} varsler løst', { n: gone.length }),
        )
      : T('Hugin: ingen nye varsler');
  const did = !after
    ? T('Slettet')
    : before.user_id !== after.user_id
      ? T('Byttet')
      : Date.parse(after.end_time) - Date.parse(after.start_time) !==
          Date.parse(before.end_time) - Date.parse(before.start_time)
        ? T('Ny lengde')
        : T('Flyttet');
  // one person: the name is in the headline, so the week line drops it
  const weeks = names.length === 1 ? short.map((l) => l.slice(l.indexOf(': ') + 2)) : short;
  const low = (x: string) => x.charAt(0).toLowerCase() + x.slice(1);
  const detail = [
    did,
    ...weeks,
    ...added.map((e) => low(ruleText(ctx.t, e))),
    ...gone.map((e) => T('løst: {text}', { text: low(ruleText(ctx.t, e)) })),
  ].join(', ');
  return {
    id: before.id,
    tone: added.length ? 'warn' : 'ok',
    title,
    lines,
    choices,
    headline,
    detail,
    balances: short,
    added: added.map((e) => ({ text: ruleText(ctx.t, e), sev: e.sev })),
    gone: gone.map((e) => ruleText(ctx.t, e)),
  };
}

/** An unprompted suggestion (issue #131 item 4): one line, where it leads, and at most one choice. */
export interface Nudge {
  key: string;
  text: string;
  shiftId?: number;
  person?: number;
  choice?: Choice;
  /** a pending holiday: the panel adds the server's count against the balance */
  absence?: ApiAbsence;
}

/**
 * What Hugin points out without being asked, for the window on screen, in this order. Open shifts
 * not yet started with their best taker; holiday asked for and not yet decided; people over and
 * under their agreed week (in «Uka» only for a week that has ended, since a week still running is
 * under by design); people with no shift at all. A Lønnsprosent day is left to the staffing row, which
 * already shows it to who may see pay.
 */
export function nudges({
  days,
  absences,
  planning,
  today,
  ...ctx
}: Ctx & {
  days: string[];
  absences: ApiAbsence[];
  planning: boolean;
  today: string;
}): Nudge[] {
  const me = ctx.me ?? null;
  const out: Nudge[] = [];
  const first = days[0] ?? today;
  const last = days.at(-1) ?? today;
  if (planning)
    for (const s of ctx.shifts
      .filter((x) => x.user_id == null && days.includes(dateOf(x)) && !started(x, ctx.now))
      .sort((a, b) => Date.parse(a.start_time) - Date.parse(b.start_time))) {
      const best = ranked(s, ctx.rows, ctx.shifts, ctx.t, ctx.showHours, ctx.me ?? null).find(quick);
      out.push({
        key: `open-${s.id}`,
        text: best
          ? T('Ledig vakt {when}: best egnet er {name}. {reason}.', {
              when: when(s),
              name: best.name,
              reason: best.reason,
            })
          : T('Ledig vakt {when}: ingen kan ta den uten noe i veien.', { when: when(s) }),
        shiftId: s.id,
        ...(best && ctx.canGive
          ? {
              choice: {
                key: `give-${s.id}-${best.userId}`,
                label: T('Se endring: {name}', { name: best.name }),
                lines: [],
                safe: best.safe,
                apply: { kind: 'give' as const, shiftId: s.id, userId: best.userId },
              },
            }
          : {}),
      });
    }
  for (const a of absences) {
    if (a.status !== 'pending' || !a.absence_type?.is_deductible) continue;
    const r = ctx.rows.find((x) => x.user.id === a.user_id);
    const end = oslo(a.end_date)?.date ?? oslo(a.start_date)?.date ?? '';
    if (!r || end < today) continue;
    out.push({
      key: `hol-${a.id}`,
      text: T('{name} ber om {type} {from} til {to}, ikke avgjort.', {
        name: fullName(r.user),
        type: (a.absence_type.name ?? T('ferie')).toLocaleLowerCase('nb'),
        from: fmtDs(oslo(a.start_date)?.date ?? ''),
        to: fmtDs(end),
      }),
      person: r.user.id,
      absence: a,
    });
  }
  if (ctx.showHours) {
    const mondays = [...new Set(days.map((d) => mondayOf(d)))].filter(
      (m) => planning || addDays(m, 6) < today,
    );
    const balances: (Nudge & { gap: number })[] = [];
    for (const m of mondays)
      for (const r of ctx.rows) {
        const norm = personOf(r).norm;
        if (norm == null) continue;
        const h = week(
          r,
          m,
          ctx.shifts.filter((x) => x.user_id === r.user.id),
        ).hours;
        const gap = round1(h - norm);
        if (h === 0 || gap === 0) continue;
        const name = fullName(r.user);
        const wk = isoWeek(m);
        const moves = gap > 0 && planning && ctx.canGive ? weekMoves(r.user.id, m, null, ctx, 1) : [];
        balances.push({
          key: `bal-${r.user.id}-${m}`,
          gap,
          text:
            gap > 0
              ? T('{name} har {h} t i uke {wk}, {gap} t over avtalt uke.', {
                  name,
                  h: num(round1(h)),
                  wk,
                  gap: num(gap),
                })
              : T('{name} har {h} t i uke {wk}, {gap} t under avtalt uke.', {
                  name,
                  h: num(round1(h)),
                  wk,
                  gap: num(-gap),
                }),
          person: r.user.id,
          ...(moves[0] ? { choice: moves[0] } : {}),
        });
      }
    // the furthest from the agreed week first, over and under alike; five are enough to act on
    out.push(...balances.sort((a, b) => Math.abs(b.gap) - Math.abs(a.gap)).slice(0, 5));
  }
  if (planning)
    for (const r of ctx.rows) {
      if (r.user.id === me || !personOf(r).norm) continue;
      if (ctx.shifts.some((x) => x.user_id === r.user.id && days.includes(dateOf(x)))) continue;
      out.push({
        key: `none-${r.user.id}`,
        text: T('{name} har ingen vakter {from} til {to}.', {
          name: fullName(r.user),
          from: fmtDs(first),
          to: fmtDs(last),
        }),
        person: r.user.id,
      });
    }
  return out;
}
